import { Component, EventEmitter, Input, Output } from '@angular/core'
import { NgClass } from '@angular/common'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'

import { corDaFase, EnvioVm, FarolVm, FaseFarolVm, StatusFase } from '../../../core/models/monitoring-os.model'

// Rótulo curto pra caber embaixo da bolinha na tabela — o nome completo da fase
// vai só no tooltip. 5 fases desde a leva 5 (entrou "Motorista notificado" como
// fase 1, as antigas 1-4 andaram um número).
const ABREVIACAO_FASE: Record<number, string> = {
  1: 'Notif.',
  2: 'Conf.',
  3: 'No local',
  4: 'Embarque',
  5: 'Finaliz.',
}

// Célula do farol — 4 bolinhas + resumo. A cor de cada bolinha é função pura de
// (fase, agora): o componente não guarda estado próprio nem dispara requisição
// pra se repintar, só recebe [agora] atualizado pela página a cada ~10s.
@Component({
  selector: 'farol-fases',
  standalone: true,
  imports: [NgClass, MatIconModule, MatTooltipModule],
  templateUrl: './farol-fases.component.html',
  styleUrl: './farol-fases.component.scss',
})
export class FarolFasesComponent {
  @Input({ required: true }) farol!: FarolVm
  @Input({ required: true }) agora!: Date
  // Objeto de no-show da linha (fora do farol — não é cor de fase, ganha de qualquer uma).
  // tipo/tipoOcorrenciaId saíram do contrato na sprint 9/10 — não existem mais dois no-shows.
  @Input() noShow: { detalhe: string | null } | null = null
  // Carimbo de quem confirmou ter enviado a OS pro motorista — null enquanto ninguém
  // confirmou. Só usado pra decidir o texto/ação do CTA da fase 1, não pinta bolinha.
  @Input() envio: EnvioVm | null = null
  @Output() confirmarFase = new EventEmitter<number>()
  @Output() confirmarEnvio = new EventEmitter<void>()

  corDaFase(fase: FaseFarolVm): StatusFase {
    return corDaFase(fase, this.agora)
  }

  abreviacao(fase: FaseFarolVm): string {
    return ABREVIACAO_FASE[fase.numero] ?? fase.nome
  }

  // Só a fase em aberto mais grave ganha a legenda de tempo — as demais só mostram
  // "registrado por" (quando confirmadas) ou ficam quietas (pendentes).
  legendaTempo(fase: FaseFarolVm): string | null {
    const cor = this.corDaFase(fase)
    if (cor === 'critico' && fase.criticoEm) {
      return `${this.minutosDesde(fase.criticoEm)} min de atraso`
    }
    if (cor === 'alerta' && fase.criticoEm) {
      return `${this.minutosAte(fase.criticoEm)} min para o crítico`
    }
    return null
  }

  registradoPor(fase: FaseFarolVm): string | null {
    if (fase.status !== 'confirmada' || fase.inferida) return null
    if (!fase.confirmadoPor) return null
    const hora = fase.confirmadoEm ? this.formatarHora(fase.confirmadoEm) : ''
    return `${fase.confirmadoPor}${hora ? ' · ' + hora : ''}`
  }

  onClickFase(fase: FaseFarolVm): void {
    this.confirmarFase.emit(fase.numero)
  }

  onConfirmarEnvio(): void {
    this.confirmarEnvio.emit()
  }

  // pendenciaDe só existe em fases abertas — a primeira fase não confirmada é a
  // única que importa pro CTA (as demais já viraram bolinha verde ou nem chegaram).
  private faseAtualAberta(): FaseFarolVm | undefined {
    return this.farol.fases.find((f) => f.status !== 'confirmada')
  }

  // 'torre' só ocorre na fase 1 sem confirmar-envio — é quando cabe o botão. Uma
  // vez enviada, a pendência vira 'motorista' (ligar) e o botão não faz mais sentido.
  mostrarConfirmarEnvio(): boolean {
    return this.faseAtualAberta()?.pendenciaDe === 'torre'
  }

  envioResumo(): string | null {
    if (!this.envio) return null
    return `Enviado ${this.formatarHora(this.envio.em)} · ${this.envio.por}`
  }

  private minutosDesde(iso: string): number {
    return Math.max(0, Math.round((this.agora.getTime() - new Date(iso).getTime()) / 60000))
  }

  private minutosAte(iso: string): number {
    return Math.max(0, Math.round((new Date(iso).getTime() - this.agora.getTime()) / 60000))
  }

  private formatarHora(iso: string): string {
    const d = new Date(iso)
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
}
